/**
 * Filtering the radar by cluster and department, and the arithmetic that has to follow it.
 *
 * ── The whole point: the KPI strip follows the filter ─────────────────────────────────────────
 * A cluster lead filtering to Operations is asking "how do MY roles stand", so "At risk: 5" has to
 * mean five Operations roles. A strip that kept reporting the company while the list below it
 * showed one cluster would be worse than no filter — two numbers on one screen describing
 * different populations, with nothing saying so. Everything the tab renders is therefore derived
 * from the filtered set, including the reconciliation totals.
 *
 * ── Why the totals are recomputed here rather than read off the payload ───────────────────────
 * `data.totals` describes the whole portfolio. Once a filter is on, every one of its six figures
 * is wrong for what is on screen, and the identity they exist to state — open requisitions =
 * unmatched + unpublished + measured — only holds if all four move together. So the filter
 * rebuilds them from the rows it kept.
 *
 * ── A posting that two clusters share matches BOTH, but only for the hires that are theirs ────
 * `cluster` is null on those (see the proxy: naming one owner would be a claim nobody made).
 * Filtering to Operations must not hide a posting Operations is half-responsible for merely
 * because Growth is on it too — so the posting is LISTED under either filter, and the totals count
 * only the requisitions that actually carry the selected assignment.
 *
 * ⚠️ Matching runs on `assignments`, the (cluster, department) PAIRS, never on `clusters` and
 * `departments` checked independently. A posting with an (Operations, Ops) requisition and a
 * (Growth, Sales) one would otherwise match (Operations, Sales) — an assignment that does not
 * exist — and contribute its whole `openHires` to that filter's totals.
 */
import type {
  BottleneckRadarData,
  RadarAssignment,
  RadarHygiene,
  RadarPosition,
  RadarTotals,
} from '../types/taBottleneck';

export interface RadarFilter {
  /** Empty string means every cluster. */
  cluster: string;
  /** Empty string means every department. */
  department: string;
}

export const NO_FILTER: RadarFilter = { cluster: '', department: '' };

export function isFiltered(filter: RadarFilter): boolean {
  return Boolean(filter.cluster || filter.department);
}

/** Roles whose cluster could not be resolved at all, grouped under one option rather than dropped. */
export const UNATTRIBUTED = '—';

const sorted = (values: Set<string>) => [...values].filter(Boolean).sort((a, b) => a.localeCompare(b));

/**
 * Every cluster on the portfolio, including one for roles nothing could place.
 *
 * Built from the WHOLE payload, never from the filtered rows: options that vanish as you use them
 * make a filter impossible to undo from the control itself.
 */
export function clusterOptions(data: BottleneckRadarData): string[] {
  const values = new Set<string>();
  let unattributed = false;
  data.positions.forEach((p) => {
    p.assignments.forEach((a) => {
      if (a.cluster) values.add(a.cluster);
      else unattributed = true;
    });
  });
  data.hygiene.unmatched.forEach((u) => {
    if (u.cluster) values.add(u.cluster);
    else unattributed = true;
  });
  return unattributed ? [...sorted(values), UNATTRIBUTED] : sorted(values);
}

export function departmentOptions(data: BottleneckRadarData): string[] {
  const values = new Set<string>();
  let unattributed = false;
  data.positions.forEach((p) => {
    p.assignments.forEach((a) => {
      if (a.department) values.add(a.department);
      else unattributed = true;
    });
  });
  data.hygiene.unmatched.forEach((u) => {
    if (u.department) values.add(u.department);
    else unattributed = true;
  });
  return unattributed ? [...sorted(values), UNATTRIBUTED] : sorted(values);
}

function matchesOne(value: string | null, want: string): boolean {
  if (!want) return true;
  if (want === UNATTRIBUTED) return !value;
  return value === want;
}

/** Both controls judged against the SAME assignment — that is the whole point of the pair. */
function assignmentMatches(a: RadarAssignment, filter: RadarFilter): boolean {
  return matchesOne(a.cluster, filter.cluster) && matchesOne(a.department, filter.department);
}

export function positionMatches(position: RadarPosition, filter: RadarFilter): boolean {
  return position.assignments.some((a) => assignmentMatches(a, filter));
}

/**
 * How many of a posting's open hires the filter actually selected.
 *
 * With no filter this is every one of them. Under one it is only the matching assignments', which
 * is why a shared posting can be listed under two clusters without its hires being counted twice
 * in either total.
 */
export function hiresInView(position: RadarPosition, filter: RadarFilter): number {
  if (!isFiltered(filter)) return position.openHires;
  return position.assignments
    .filter((a) => assignmentMatches(a, filter))
    .reduce((total, a) => total + a.hires, 0);
}

export interface FilteredRadar {
  positions: RadarPosition[];
  unmatched: RadarHygiene['unmatched'];
  totals: RadarTotals;
}

/**
 * The rows and the arithmetic for one filter.
 *
 * ⚠️ `unpublishedRequisitions` is recomputed from the kept rows and NOT taken from
 * `hygiene.unpublished`, which counts the whole portfolio. Reusing it under a filter was the same
 * class of mistake as the header that said "22 open roles" when it meant 22 pipelines.
 */
export function applyFilter(data: BottleneckRadarData, filter: RadarFilter): FilteredRadar {
  const positions = data.positions.filter((p) => positionMatches(p, filter));
  const unmatched = data.hygiene.unmatched.filter(
    (u) => matchesOne(u.cluster, filter.cluster) && matchesOne(u.department, filter.department)
  );

  const sumHires = (list: RadarPosition[]) =>
    list.reduce((total, p) => total + hiresInView(p, filter), 0);
  const measured = positions.filter((p) => p.measured);
  const unmeasured = positions.filter((p) => !p.measured);

  return {
    positions,
    unmatched,
    totals: {
      openRequisitions: unmatched.length + sumHires(positions),
      unmatchedRequisitions: unmatched.length,
      unpublishedRequisitions: sumHires(unmeasured),
      measuredRequisitions: sumHires(measured),
      postings: positions.length,
      measuredPostings: measured.length,
    },
  };
}
